import React, { useRef, useState, useEffect } from "react";
import { useGoogleMaps } from "../../contexts/GoogleMapsContext";
import "./MapWithMarker.scss";

interface MapWithMarkerProps {
  initialLatLng?: { lat: number; lng: number };
  initialName?: string;
  initialAddress?: string;
  onSave?: (data: {
    name: string;
    address: string;
    lat: number;
    lng: number;
  }) => void;
  onReset?: () => void;
  mode?: "view" | "edit" | "create";
}

const MapWithMarker: React.FC<MapWithMarkerProps> = ({
  initialLatLng = { lat: 19.076, lng: 72.8777 }, // Mumbai default
  initialName = "",
  initialAddress = "",
  onSave,
  onReset,
  mode = "create",
}) => {
  const google = useGoogleMaps();
  const mapRef = useRef<HTMLDivElement>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);
  const autocompleteRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(initialName);
  const [address, setAddress] = useState(initialAddress);
  const [latLng, setLatLng] = useState(initialLatLng);
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
  const [errors, setErrors] = useState<{ name?: string; address?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize map and marker ONCE
  useEffect(() => {
    if (google && mapRef.current && !mapInstance) {
      const map = new window.google.maps.Map(mapRef.current, {
        center: latLng,
        zoom: 14,
        streetViewControl: false,
      });
      setMapInstance(map);

      // Create marker
      const marker = new window.google.maps.Marker({
        position: latLng,
        map,
        draggable: mode !== "view",
        title: name || "Location",
      });
      markerRef.current = marker;

      // Marker drag event
      marker.addListener("dragend", (event: google.maps.MapMouseEvent) => {
        if (event.latLng) {
          const newLatLng = {
            lat: event.latLng.lat(),
            lng: event.latLng.lng(),
          };
          setLatLng(newLatLng);
          // Animate pan
          map.panTo(newLatLng);
          // Reverse geocode to get address
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: newLatLng }, (results, status) => {
            if (status === "OK" && results && results[0]) {
              setAddress(results[0].formatted_address);
            }
          });
        }
      });

      // Autocomplete setup
      if (autocompleteRef.current) {
        const autocomplete = new window.google.maps.places.Autocomplete(
          autocompleteRef.current,
          {
            types: ["geocode"],
          }
        );
        autocomplete.addListener("place_changed", () => {
          const place = autocomplete.getPlace();
          if (place.geometry && place.geometry.location) {
            const newLatLng = {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
            };
            setLatLng(newLatLng);
            map.panTo(newLatLng);
            marker.setPosition(newLatLng);
            setAddress(place.formatted_address || "");
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [google]);

  // Update marker and map position when latLng changes (but don't recreate map)
  useEffect(() => {
    if (markerRef.current && mapInstance) {
      markerRef.current.setPosition(latLng);
      // Animate pan for smooth transition
      mapInstance.panTo(latLng);
    }
  }, [latLng, mapInstance]);

  // Update marker position when latLng changes
  useEffect(() => {
    if (markerRef.current && mapInstance) {
      markerRef.current.setPosition(latLng);
      mapInstance.panTo(latLng);
    }
  }, [latLng, mapInstance]);

  // Reset handler
  const handleReset = () => {
    setName(initialName);
    setLatLng(initialLatLng);
    setAddress("");
    if (onReset) onReset();
  };

  // Validate fields
  const validate = () => {
    const newErrors: { name?: string; address?: string } = {};
    if (!name.trim()) newErrors.name = "Name is required";
    if (!address.trim()) newErrors.address = "Address is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Save handler
  const handleSave = () => {
    setIsSubmitting(true);
    if (!validate()) {
      setIsSubmitting(false);
      return;
    }
    if (onSave) {
      onSave({ name, address, lat: latLng.lat, lng: latLng.lng });
    }
    setIsSubmitting(false);
  };

  return (
    <div className="map-with-marker-container">
      <div className="map-with-marker-fields">
        <div className="map-with-marker-form-fields">
          <div className="form-group">
            <label htmlFor="address-input" className="map-with-marker-label">
              Address <span style={{ color: "red" }}>*</span>
            </label>
            <input
              id="address-input"
              ref={autocompleteRef}
              type="text"
              placeholder="Search Address..."
              value={address}
              onChange={(e) => {
                setAddress(e.target.value);
                if (errors.address)
                  setErrors((prev) => ({ ...prev, address: undefined }));
              }}
              className={`map-with-marker-input address-input${
                errors.address ? " error" : ""
              }`}
              disabled={mode === "view"}
            />
            <div className="form-error">
              {errors.address ? errors.address : ""}
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="name-input" className="map-with-marker-label">
              Name <span style={{ color: "red" }}>*</span>
            </label>
            <input
              id="name-input"
              type="text"
              placeholder="Name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errors.name)
                  setErrors((prev) => ({ ...prev, name: undefined }));
              }}
              className={`map-with-marker-input name-input${
                errors.name ? " error" : ""
              }`}
              disabled={mode === "view"}
            />
            <div className="form-error">{errors.name ? errors.name : ""}</div>
          </div>
        </div>
        {mode !== "view" && (
          <div className="map-with-marker-buttons flex gap-4">
            <button
              type="button"
              onClick={handleSave}
              className="primary_button w-48"
              disabled={isSubmitting}
            >
              Save
            </button>
            <button
              type="button"
              onClick={handleReset}
              className="secondary_button w-48"
              disabled={isSubmitting}
            >
              Reset
            </button>
          </div>
        )}
      </div>
      <div ref={mapRef} className="map-with-marker-map" />
    </div>
  );
};

export default MapWithMarker;
